import React, { useEffect, useState } from "react";
import {
  LinearProgress,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Box,
  Menu,
  MenuItem,
  ListItemText,
  Typography,
  IconButton,
  CircularProgress,
  ListItemIcon,
  Chip,
} from "@mui/material";
import http from "../../../utils/http";
import { errorToastMessage, toastMessage } from "../../../utils/toast";
import {
  NoDataContainer,
  pageSize,
  StyledTableCell,
  TableBorderRadiusTopLeftRight,
} from "../../Common/styles/table";
import { AxiosResponse } from "axios";
import StatusBadge from "../../Common/UI/StatusTag";
import { useNavigate } from "react-router-dom";
import { MoreVert } from "@mui/icons-material";
import AddNewUserModal from "./AddNewUserModal";
import { useAppSelector } from "../../../Redux/hooks";
import DataTableLayout from "../../Common/UI/DataTableLayout";
import DataTableFooter from "../../Common/UI/DataTableFooter";
import { dataTableListShell } from "../../../utils/dataTableShell";

// Derives the display status from the two backend status fields:
//  - inviteStatus (platform_users.status): invited | active
//  - accountStatus (users.status):         active  | inactive
const getStatusBadge = (
  inviteStatus: string,
  accountStatus: string
): { label: string; variant: "active" | "pending" | "cancelled" } => {
  if (inviteStatus === "invited") {
    return { label: "Invited", variant: "pending" };
  }
  if (accountStatus === "inactive") {
    return { label: "Inactive", variant: "cancelled" };
  }
  return { label: "Active", variant: "active" };
};

type Props = {
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  searchText: string;
  setSearchText: React.Dispatch<React.SetStateAction<string>>;
  canEdit: boolean;
};

const UsersList = ({
  searchText,
  showModal,
  setShowModal,
  canEdit,
}: Props) => {
  const navigate = useNavigate();
  const currentUserId = useAppSelector((state) => state.user.userId);
  const [loading, setLoading] = useState(true);
  const [toggleLoader, setToggleLoader] = useState(false);
  const [studyData, setStudyData] = useState<any>([]);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [menuLoader, setMenuLoader] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [usersCount, setUsersCount] = useState<number>(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  useEffect(() => {
    const fetchStudyData = async () => {
      try {
        setLoading(true);
        let url = `/platform/users/list?page=${page + 1}&size=${pageSize}`;
        if (searchText) {
          url += `&query=${searchText}`;
        }

        const res: AxiosResponse = await http.get(url);
        const data = res.data?.data?.rows;
        const newData = data?.map((item: any) => ({
          id: item?.id,
          userId: item?.userId || "",
          firstName: item?.user?.firstName || "-",
          lastName: item?.user?.lastName || "-",
          email: item?.user?.email || "-",
          inviteStatus: item?.status || "-",
          accountStatus: item?.user?.status || "-",
          roles:
            item?.platformRoles
              ?.map((pr: any) => pr?.role)
              ?.filter((r: any) => r) || [],
        }));

        setStudyData(newData || []);
        setUsersCount(res.data?.data?.count || 0);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        errorToastMessage(err as Error);
      }
    };
    fetchStudyData();
  }, [searchText, toggleLoader, page]);

  const refreshPage = () => {
    setToggleLoader((prev) => !prev);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, row: any) => {
    setSelectedRow(row);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleRowClick = (userId: string) => {
    if (userId) {
      navigate(userId);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedRow(null);
  };

  const handlePageChange = (_: any, newPage: number) => {
    setPage(newPage);
  };

  const reinviteUser = async () => {
    try {
      setMenuLoader(true);
      const body = {
        userId: selectedRow?.userId,
      };
      const res: AxiosResponse = await http.post(
        `/platform/users/reinvite`,
        body
      );
      toastMessage("success", res.data.message);
      setMenuLoader(false);
      handleMenuClose();
      refreshPage();
    } catch (err) {
      setMenuLoader(false);
      errorToastMessage(err as Error);
    }
  };

  const modifyAccessStatus = async (status: "active" | "inactive") => {
    try {
      setMenuLoader(true);
      const res: AxiosResponse = await http.post(
        `/platform/users/${selectedRow?.userId}/access-status`,
        { status }
      );
      toastMessage("success", res.data.message);
      setMenuLoader(false);
      handleMenuClose();
      refreshPage();
    } catch (err) {
      setMenuLoader(false);
      errorToastMessage(err as Error);
    }
  };

  // Whether the kebab menu has any actionable item for this row.
  const hasRowActions = (row: any) =>
    canEdit &&
    (row?.inviteStatus === "invited" ||
      row?.accountStatus === "active" ||
      row?.accountStatus === "inactive");

  return (
    <Box
      sx={{
        ...dataTableListShell,
        p: 2,
        backgroundColor: "#fff",
        borderRadius: "10px",
      }}
    >
      <DataTableLayout
        footer={
          <DataTableFooter
            filteredCount={usersCount}
            page={page}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            loading={loading}
          />
        }
      >
        <Table
          sx={TableBorderRadiusTopLeftRight}
          stickyHeader
          className="scrollable-table"
        >
        <TableHead>
          <TableRow>
            <StyledTableCell>First Name</StyledTableCell>
            <StyledTableCell>Last Name</StyledTableCell>
            <StyledTableCell>Email</StyledTableCell>
            <StyledTableCell>Role(s)</StyledTableCell>
            <StyledTableCell>Status</StyledTableCell>
            <StyledTableCell />
          </TableRow>
        </TableHead>
        {!loading && studyData?.length > 0 && (
          <>
            <TableBody>
              {studyData?.map((row: any) => {
                const badge = getStatusBadge(
                  row?.inviteStatus,
                  row?.accountStatus
                );
                return (
                  <TableRow key={row?.id}>
                    <StyledTableCell
                      onClick={() => handleRowClick(row?.userId)}
                      sx={{ cursor: "pointer", color: "primary.main" }}
                    >
                      {row?.firstName}
                    </StyledTableCell>
                    <StyledTableCell
                      onClick={() => handleRowClick(row?.userId)}
                      sx={{ cursor: "pointer", color: "primary.main" }}
                    >
                      {row?.lastName}
                    </StyledTableCell>
                    <StyledTableCell>{row?.email}</StyledTableCell>
                    <StyledTableCell>
                      {row?.roles?.length > 0 ? (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}
                        >
                          {row.roles.map((role: any) => (
                            <Chip
                              key={role?.id}
                              label={role?.label}
                              size="small"
                            />
                          ))}
                        </Box>
                      ) : (
                        "-"
                      )}
                    </StyledTableCell>
                    <StyledTableCell>
                      <StatusBadge label={badge.label} variant={badge.variant} />
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      {hasRowActions(row) && (
                        <IconButton onClick={(e) => handleMenuClick(e, row)}>
                          <MoreVert />
                        </IconButton>
                      )}
                    </StyledTableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </>
        )}
      </Table>
      {!loading && studyData.length === 0 && (
        <NoDataContainer>
          <Typography variant="body1" color="gray">
            No Data
          </Typography>
        </NoDataContainer>
      )}
      {loading && (
        <Box sx={{ width: "100%" }}>
          <LinearProgress />
        </Box>
      )}
      </DataTableLayout>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        PaperProps={{
          style: {
            maxHeight: "400px",
            width: "20ch",
          },
        }}
      >
        {selectedRow?.inviteStatus === "invited" && (
          <MenuItem disabled={menuLoader} onClick={reinviteUser}>
            <ListItemText>Reinvite</ListItemText>
            {menuLoader && (
              <ListItemIcon>
                <CircularProgress size={18} sx={{ ml: 1 }} />
              </ListItemIcon>
            )}
          </MenuItem>
        )}
        {selectedRow?.accountStatus === "active" &&
          selectedRow?.userId !== currentUserId && (
            <MenuItem
              disabled={menuLoader}
              onClick={() => modifyAccessStatus("inactive")}
            >
              <ListItemText>Deactivate</ListItemText>
              {menuLoader && (
                <ListItemIcon>
                  <CircularProgress size={18} sx={{ ml: 1 }} />
                </ListItemIcon>
              )}
            </MenuItem>
          )}
        {selectedRow?.accountStatus === "inactive" && (
          <MenuItem
            disabled={menuLoader}
            onClick={() => modifyAccessStatus("active")}
          >
            <ListItemText>Activate</ListItemText>
            {menuLoader && (
              <ListItemIcon>
                <CircularProgress size={18} sx={{ ml: 1 }} />
              </ListItemIcon>
            )}
          </MenuItem>
        )}
      </Menu>

      {showModal && (
        <AddNewUserModal
          showModal={showModal}
          closeModal={closeModal}
          data={selectedRow}
          refreshPage={refreshPage}
        />
      )}
    </Box>
  );
};

export default UsersList;
